/**
 * Training Level Detection System
 * 
 * Automatically determines user's training level from multiple signals:
 * - Operator Score (primary)
 * - Training history (sessions, time in system)
 * - Relative strength (1RMs vs bodyweight)
 * - Performance patterns (RPE, progression rate)
 * 
 * Provides single source of truth for user's training level across all systems.
 */

import { strengthBandsFor } from './strengthStandards';

export type TrainingLevel = 'beginner' | 'intermediate' | 'advanced' | 'elite';

export interface TrainingLevelResult {
  level: TrainingLevel;
  confidence: number; // 0-1
  reasoning: string[];
  scores: {
    operatorScore: number;
    strengthScore: number;
    experienceScore: number;
    progressionScore: number;
  };
}

export interface TrainingLevelParams {
  // Primary signal
  operatorScore: number;
  
  // Training history
  totalSessions: number;
  timeInSystemDays: number;
  
  // Performance metrics
  avgRPE?: number;
  progressionRate?: number; // % per session

  /** What the user told us during onboarding. Used ONLY as a day-one prior and
   *  decayed to zero over PRIOR_DECAY_SESSIONS as measured signal replaces it. */
  selfReportedLevel?: TrainingLevel;

  /** Biological sex, for relative-strength banding. Bodyweight-strength ratios
   *  differ by sex; without this the male-normed bands under-score women. */
  sex?: 'male' | 'female';

  // Strength metrics
  oneRMs?: {
    deadlift?: number;
    squat?: number;
    benchPress?: number;
  };
  bodyweight?: number;
}

/**
 * Detect training level from multiple signals
 */
export function detectTrainingLevel(params: TrainingLevelParams): TrainingLevelResult {
  const reasoning: string[] = [];
  const scores = {
    operatorScore: 0,
    strengthScore: 0,
    experienceScore: 0,
    progressionScore: 0,
  };
  
  // ============================================================================
  // 1. OPERATOR SCORE (Primary Signal - 40% weight)
  // ============================================================================
  let operatorLevel: TrainingLevel;
  if (params.operatorScore >= 85) {
    operatorLevel = 'elite';
    scores.operatorScore = 4;
    reasoning.push(`Operator Score: ${params.operatorScore} (Elite range 85+)`);
  } else if (params.operatorScore >= 70) {
    operatorLevel = 'advanced';
    scores.operatorScore = 3;
    reasoning.push(`Operator Score: ${params.operatorScore} (Advanced range 70-84)`);
  } else if (params.operatorScore >= 55) {
    operatorLevel = 'intermediate';
    scores.operatorScore = 2;
    reasoning.push(`Operator Score: ${params.operatorScore} (Intermediate range 55-69)`);
  } else {
    operatorLevel = 'beginner';
    scores.operatorScore = 1;
    reasoning.push(`Operator Score: ${params.operatorScore} (Beginner range <55)`);
  }
  
  // ============================================================================
  // 2. RELATIVE STRENGTH (30% weight)
  // ============================================================================
  let strengthLevel: TrainingLevel = operatorLevel; // Default to operator level
  
  if (params.oneRMs && params.bodyweight && params.bodyweight > 0) {
    const ratios = {
      deadlift: params.oneRMs.deadlift ? params.oneRMs.deadlift / params.bodyweight : 0,
      squat: params.oneRMs.squat ? params.oneRMs.squat / params.bodyweight : 0,
      bench: params.oneRMs.benchPress ? params.oneRMs.benchPress / params.bodyweight : 0,
    };
    
    // Calculate average ratio (only count non-zero)
    const validRatios = Object.values(ratios).filter(r => r > 0);
    if (validRatios.length > 0) {
      const avgRatio = validRatios.reduce((sum, r) => sum + r, 0) / validRatios.length;

      // Sex-specific bands. Bodyweight-strength ratios differ by sex, so a single
      // set of cutoffs under-scores women and over-scores men. Bands are derived
      // from the same standards table the day-one seeds use, so seed and
      // classifier agree by construction (see strengthBandsFor).
      const bands = strengthBandsFor(params.sex);
      const sexNote = params.sex ? ` (${params.sex} standard)` : '';

      if (avgRatio >= bands.elite) {
        strengthLevel = 'elite';
        scores.strengthScore = 4;
        reasoning.push(`Relative Strength: ${avgRatio.toFixed(2)}x BW (Elite level${sexNote})`);
      }
      else if (avgRatio >= bands.advanced) {
        strengthLevel = 'advanced';
        scores.strengthScore = 3;
        reasoning.push(`Relative Strength: ${avgRatio.toFixed(2)}x BW (Advanced level${sexNote})`);
      }
      else if (avgRatio >= bands.intermediate) {
        strengthLevel = 'intermediate';
        scores.strengthScore = 2;
        reasoning.push(`Relative Strength: ${avgRatio.toFixed(2)}x BW (Intermediate level${sexNote})`);
      }
      else {
        strengthLevel = 'beginner';
        scores.strengthScore = 1;
        reasoning.push(`Relative Strength: ${avgRatio.toFixed(2)}x BW (Beginner level${sexNote})`);
      }
    }
  }
  
  // ============================================================================
  // 3. TRAINING EXPERIENCE (20% weight)
  // ============================================================================
  let experienceLevel: TrainingLevel;
  
  // Override: Always beginner if <20 sessions
  if (params.totalSessions < 20) {
    experienceLevel = 'beginner';
    scores.experienceScore = 1;
    reasoning.push(`Training History: ${params.totalSessions} sessions (Building foundation)`);
  }
  // Elite potential: 100+ sessions over 6+ months
  else if (params.totalSessions >= 100 && params.timeInSystemDays >= 180) {
    experienceLevel = 'elite';
    scores.experienceScore = 4;
    reasoning.push(`Training History: ${params.totalSessions} sessions over ${Math.floor(params.timeInSystemDays / 30)} months (Elite consistency)`);
  }
  // Advanced potential: 50+ sessions over 3+ months
  else if (params.totalSessions >= 50 && params.timeInSystemDays >= 90) {
    experienceLevel = 'advanced';
    scores.experienceScore = 3;
    reasoning.push(`Training History: ${params.totalSessions} sessions over ${Math.floor(params.timeInSystemDays / 30)} months (Advanced consistency)`);
  }
  // Intermediate: 20-50 sessions
  else {
    experienceLevel = 'intermediate';
    scores.experienceScore = 2;
    reasoning.push(`Training History: ${params.totalSessions} sessions (Intermediate experience)`);
  }
  
  // ============================================================================
  // 4. PROGRESSION PATTERNS (10% weight)
  // ============================================================================
  let progressionLevel: TrainingLevel = operatorLevel; // Default
  
  if (params.progressionRate !== undefined && params.avgRPE !== undefined) {
    // Elite: Slow progression (1-1.5%), controlled RPE (7-8)
    if (params.progressionRate >= 0.01 && params.progressionRate <= 0.015 && params.avgRPE >= 7 && params.avgRPE <= 8) {
      progressionLevel = 'elite';
      scores.progressionScore = 4;
      reasoning.push(`Progression: ${(params.progressionRate * 100).toFixed(1)}%/session at RPE ${params.avgRPE.toFixed(1)} (Elite micro-loading)`);
    }
    // Advanced: Moderate progression (1.5-2.5%), controlled RPE
    else if (params.progressionRate >= 0.015 && params.progressionRate <= 0.025 && params.avgRPE >= 6.5 && params.avgRPE <= 8.5) {
      progressionLevel = 'advanced';
      scores.progressionScore = 3;
      reasoning.push(`Progression: ${(params.progressionRate * 100).toFixed(1)}%/session at RPE ${params.avgRPE.toFixed(1)} (Advanced progression)`);
    }
    // Intermediate: Standard progression (2.5-4%)
    else if (params.progressionRate >= 0.025 && params.progressionRate <= 0.04) {
      progressionLevel = 'intermediate';
      scores.progressionScore = 2;
      reasoning.push(`Progression: ${(params.progressionRate * 100).toFixed(1)}%/session (Intermediate progression)`);
    }
    // Beginner: Fast progression (4%+) or inconsistent
    else {
      progressionLevel = 'beginner';
      scores.progressionScore = 1;
      reasoning.push(`Progression: ${(params.progressionRate * 100).toFixed(1)}%/session (Beginner gains)`);
    }
  }
  
  // ============================================================================
  // 5. WEIGHTED CONSENSUS
  // ============================================================================
  const weights = {
    operator: 0.40,
    strength: 0.30,
    experience: 0.20,
    progression: 0.10,
  };
  
  let weightedScore =
    (scores.operatorScore * weights.operator) +
    (scores.strengthScore * weights.strength) +
    (scores.experienceScore * weights.experience) +
    (scores.progressionScore * weights.progression);

  // ── Day-one prior ─────────────────────────────────────────────────────────
  // Three of the four signals above are measurements of what the user has done
  // INSIDE this app. On day one they have done nothing, so operatorScore is 0
  // and `totalSessions < 20` hard-pins experienceScore to beginner — 60% of the
  // blend is nailed to the floor before we look at a single lift. The result:
  // someone with fifteen years under the bar was scored a beginner and handed
  // the 45 lb barbell floor, and the experience level they self-reported during
  // onboarding never reached the load math at all.
  //
  // The error was treating NO DATA as evidence of being a novice. It isn't
  // evidence of anything. Until we have our own, the user's own account of
  // themselves is the best prior available — so we lean on it when we know
  // nothing, and let it decay to zero as real sessions replace it with measured
  // signal. By PRIOR_DECAY_SESSIONS the app is running entirely on what it has
  // actually observed, and a self-report that turns out to be wrong is washed
  // out by the same data that proves it wrong.
  //
  // Over-claiming is bounded: the level only sets loads for exercises with no
  // related 1RM to derive from, every prescription is still %1RM of a seed the
  // user can edit, and the RPE gate corrects from the first working set.
  const PRIOR_DECAY_SESSIONS = 20;
  if (params.selfReportedLevel) {
    const selfScore = ['beginner', 'intermediate', 'advanced', 'elite'].indexOf(params.selfReportedLevel) + 1;
    const priorWeight = Math.max(0, Math.min(1, 1 - params.totalSessions / PRIOR_DECAY_SESSIONS));
    if (priorWeight > 0) {
      weightedScore = (selfScore * priorWeight) + (weightedScore * (1 - priorWeight));
      reasoning.push(
        `Self-reported: ${params.selfReportedLevel} (weighted ${Math.round(priorWeight * 100)}% — decays as sessions are logged)`,
      );
    }
  }

  // Map weighted score to level
  let finalLevel: TrainingLevel;
  if (weightedScore >= 3.5) {
    finalLevel = 'elite';
  } else if (weightedScore >= 2.5) {
    finalLevel = 'advanced';
  } else if (weightedScore >= 1.5) {
    finalLevel = 'intermediate';
  } else {
    finalLevel = 'beginner';
  }
  
  // ============================================================================
  // 6. CONFIDENCE CALCULATION
  // ============================================================================
  // High confidence: All signals agree (within 1 level)
  // Medium confidence: Some disagreement (within 2 levels)
  // Low confidence: Major disagreement or insufficient data
  
  const levels = [operatorLevel, strengthLevel, experienceLevel, progressionLevel];
  const levelValues = levels.map(l => ['beginner', 'intermediate', 'advanced', 'elite'].indexOf(l));
  const maxDiff = Math.max(...levelValues) - Math.min(...levelValues);
  
  let confidence: number;
  if (maxDiff === 0) {
    confidence = 1.0; // Perfect agreement
  } else if (maxDiff === 1) {
    confidence = 0.85; // Close agreement
  } else if (maxDiff === 2) {
    confidence = 0.6; // Some disagreement
  } else {
    confidence = 0.4; // Major disagreement
  }
  
  // Reduce confidence if missing data
  if (!params.oneRMs || !params.bodyweight) confidence *= 0.9;
  if (params.progressionRate === undefined || params.avgRPE === undefined) confidence *= 0.9;
  if (params.totalSessions < 10) confidence *= 0.7;
  
  return {
    level: finalLevel,
    confidence,
    reasoning,
    scores,
  };
}

/**
 * Get training level thresholds for various systems
 */
export function getTrainingLevelThresholds(level: TrainingLevel) {
  const thresholds = {
    beginner: {
      acwr: { min: 0.8, max: 1.5 },
      progressionRate: 0.05, // 5% per session
      volumeIncrease: 0.10, // 10% per week
      frequencyTarget: 3, // sessions/week
      restDays: 2, // days between sessions
    },
    intermediate: {
      acwr: { min: 0.8, max: 1.35 },
      progressionRate: 0.025, // 2.5% per session
      volumeIncrease: 0.05, // 5% per week
      frequencyTarget: 4,
      restDays: 1.5,
    },
    advanced: {
      acwr: { min: 0.75, max: 1.4 },
      progressionRate: 0.015, // 1.5% per session
      volumeIncrease: 0.03, // 3% per week
      frequencyTarget: 5,
      restDays: 1,
    },
    elite: {
      acwr: { min: 0.7, max: 1.5 },
      progressionRate: 0.01, // 1% per session (micro-loading)
      volumeIncrease: 0.02, // 2% per week
      frequencyTarget: 6,
      restDays: 1,
    },
  };
  
  return thresholds[level];
}

/**
 * Get ML normalization values based on training level
 */
export function getMLNormalizationValues(level: TrainingLevel) {
  const normalization = {
    beginner: {
      volume7d: 25000,
      volume30d: 100000,
    },
    intermediate: {
      volume7d: 50000,
      volume30d: 200000,
    },
    advanced: {
      volume7d: 75000,
      volume30d: 300000,
    },
    elite: {
      volume7d: 100000,
      volume30d: 400000,
    },
  };
  
  return normalization[level];
}

